import type {
  IPlaybackScheduleRoot,
  IScheduleRootAssignment
} from '../models/player-registration-payload'

/**
 * Canonical schedule-root URN from a playback root (API `scheduleRootUrn`, legacy `campaignUrn`, or registration `urn`).
 */
export function getScheduleRootIdentityUrn(root: IPlaybackScheduleRoot | null): string | null {
  if (root == null) {
    return null
  }
  const r = root as { scheduleRootUrn?: unknown, campaignUrn?: unknown, urn?: unknown }
  if (typeof r.scheduleRootUrn === 'string' && r.scheduleRootUrn !== '') {
    return r.scheduleRootUrn
  }
  if (typeof r.campaignUrn === 'string' && r.campaignUrn !== '') {
    return r.campaignUrn
  }
  if (typeof r.urn === 'string' && r.urn !== '') {
    return r.urn
  }
  return null
}

/**
 * True when the root carries a schedule-root identity (`scheduleRootUrn`, legacy `campaignUrn`, or registration `urn`).
 */
export function isScheduleRootAssignment(
  root: IPlaybackScheduleRoot | null
): root is IScheduleRootAssignment {
  return getScheduleRootIdentityUrn(root) != null
}

function playlistSpineForIdentity(root: IPlaybackScheduleRoot): string {
  const playlists = [...(root.playlists ?? [])].sort((a, b) => a.index - b.index)
  return playlists.map(p => `${p.index}:${p.playlistUrn}`).join('|')
}

/**
 * Stable id for comparing “same logical playback assignment” across registration refreshes.
 * Schedule-root assignments include the ordered playlist spine so merges (e.g. operator default) change identity.
 */
export function getPlaybackScheduleMaterializationIdentity(
  root: IPlaybackScheduleRoot | null
): string | null {
  if (root == null) {
    return null
  }
  if (isScheduleRootAssignment(root)) {
    const id = getScheduleRootIdentityUrn(root)
    if (id == null) {
      return null
    }
    const spine = playlistSpineForIdentity(root)
    return spine !== '' ? `${id}|${spine}` : id
  }
  const spine = playlistSpineForIdentity(root)
  const seq = root.customSequence?.join('\0') ?? ''
  return `schedule:${root.playbackMode}:${seq}:${spine}`
}
